import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Scan,
  CheckCircle,
  Users,
  TrendingUp,
  Calendar,
  ArrowRight,
  QrCode,
  Download,
  Loader2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatNumber } from '@utils';
import api from '@services/api';

interface CheckinStats {
  total_tickets: number;
  checked_in: number;
  remaining: number;
  checkin_rate: number;
  events: Array<{
    id: number;
    name: string;
    start_date: string;
    capacity: number;
    sold_count: number;
  }>;
}

export default function CheckinPage() {
  const [selectedEvent, setSelectedEvent] = useState('all');

  const { data: statsData, isLoading } = useQuery({
    queryKey: ['checkin-stats', selectedEvent],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (selectedEvent !== 'all') params.event_id = selectedEvent;
      const response = await api.get<{ data: CheckinStats }>('/checkin/stats', params);
      return response.data;
    },
  });

  const stats = statsData;
  const events = stats?.events || [];

  // Build mock hourly chart data based on real checked_in count
  const checkedIn = stats?.checked_in || 0;
  const checkinData = checkedIn > 0
    ? [
      { time: '9:00', checkins: Math.round(checkedIn * 0.05) },
      { time: '10:00', checkins: Math.round(checkedIn * 0.15) },
      { time: '11:00', checkins: Math.round(checkedIn * 0.25) },
      { time: '12:00', checkins: Math.round(checkedIn * 0.38) },
      { time: '13:00', checkins: Math.round(checkedIn * 0.6) },
      { time: '14:00', checkins: checkedIn },
    ]
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Check-in Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Monitor ticket validation and entry tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          {events.length > 0 && (
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="input py-2 text-sm"
              aria-label="Filter by Event"
            >
              <option value="all">All Events</option>
              {events.map((e) => (
                <option key={e.id} value={String(e.id)}>{e.name}</option>
              ))}
            </select>
          )}
          <Link to="/admin/scanner" className="btn-primary">
            <Scan className="w-4 h-4" />
            Open Scanner
          </Link>
        </div>
      </div>

      {/* Stats */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 dark:bg-gray-700 mb-3" />
              <div className="h-8 bg-gray-200 rounded w-3/4 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="stat-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total Tickets</p>
                <p className="stat-value">{formatNumber(stats?.total_tickets || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 dark:bg-primary-900/20">
                <QrCode className="w-6 h-6" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="stat-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Checked In</p>
                <p className="stat-value">{formatNumber(stats?.checked_in || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-success-100 flex items-center justify-center text-success-600 dark:bg-success-900/20">
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="stat-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Remaining</p>
                <p className="stat-value">{formatNumber(stats?.remaining || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-warning-100 flex items-center justify-center text-warning-600 dark:bg-warning-900/20">
                <Users className="w-6 h-6" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="stat-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Check-in Rate</p>
                <p className="stat-value">{stats?.checkin_rate || 0}%</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center text-accent-600 dark:bg-accent-900/20">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Charts & Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Check-in Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Check-in Activity</h3>
          </div>
          <div className="h-64">
            {checkinData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={checkinData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="time" stroke="#9CA3AF" fontSize={12} />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="checkins" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                {isLoading ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : (
                  <p>No check-in data yet</p>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Active Events */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Active Events</h3>
            <Link to="/admin/events" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
              View all
            </Link>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-500" />
              </div>
            ) : events.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No active events</p>
              </div>
            ) : (
              events.map((event) => {
                const rate = event.capacity > 0 ? Math.round((event.sold_count / event.capacity) * 100) : 0;
                return (
                  <div key={event.id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-success-100 flex items-center justify-center text-success-600 dark:bg-success-900/20">
                        <Calendar className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{event.name}</p>
                        <p className="text-sm text-gray-500">{event.sold_count} / {event.capacity} tickets sold</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{rate}%</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/admin/scanner"
            className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-colors dark:border-gray-700 dark:hover:border-primary-400 dark:hover:bg-primary-900/20"
          >
            <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 dark:bg-primary-900/20">
              <Scan className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Start Scanning</p>
              <p className="text-sm text-gray-500">Open the QR scanner</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
          </Link>

          <Link
            to="/admin/reports"
            className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-success-500 hover:bg-success-50 transition-colors dark:border-gray-700 dark:hover:border-success-400 dark:hover:bg-success-900/20"
          >
            <div className="w-12 h-12 rounded-lg bg-success-100 flex items-center justify-center text-success-600 dark:bg-success-900/20">
              <Download className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">View Reports</p>
              <p className="text-sm text-gray-500">See check-in analytics</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
          </Link>

          <Link
            to="/admin/settings/team"
            className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-accent-500 hover:bg-accent-50 transition-colors dark:border-gray-700 dark:hover:border-accent-400 dark:hover:bg-accent-900/20"
          >
            <div className="w-12 h-12 rounded-lg bg-accent-100 flex items-center justify-center text-accent-600 dark:bg-accent-900/20">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Manage Staff</p>
              <p className="text-sm text-gray-500">Add check-in operators</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
